import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import {
  downloadPreparedMixesReport,
  getApiErrorMessage,
  getPreparedMixesReport,
} from '../../../../api/mix-deliveriesApi';

const INITIAL_FILTERS = {
  preparedFrom: '',
  preparedTo: '',
  search: '',
  sandwichingRequired: '',
  changedAfterPreparation: '',
  requiresReview: '',
  isActive: '',
};

const EMPTY_SUMMARY = {
  totalPreparations: 0,
  totalMixItems: 0,
  totalMixQuantity: 0,
};

const EMPTY_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
};

export default function MixPreparedReport({ refreshKey = 0 }) {
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS);

  /*
   * TablePagination korzysta z numerowania
   * stron od zera, backend od jednego.
   */
  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [rows, setRows] = useState([]);

  const [summary, setSummary] = useState(EMPTY_SUMMARY);

  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const [loading, setLoading] = useState(true);

  const [exporting, setExporting] = useState(false);

  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getPreparedMixesReport({
        ...appliedFilters,

        page: page + 1,
        limit: rowsPerPage,
      });

      setRows(Array.isArray(result?.data) ? result.data : []);

      setSummary({
        totalPreparations: Number(result?.summary?.totalPreparations ?? 0),

        totalMixItems: Number(result?.summary?.totalMixItems ?? 0),

        totalMixQuantity: Number(result?.summary?.totalMixQuantity ?? 0),
      });

      setPagination({
        page: Number(result?.pagination?.page ?? page + 1),

        limit: Number(result?.pagination?.limit ?? rowsPerPage),

        total: Number(result?.pagination?.total ?? 0),

        totalPages: Number(result?.pagination?.totalPages ?? 0),
      });
    } catch (loadError) {
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      setPagination(EMPTY_PAGINATION);

      setError(getApiErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, rowsPerPage]);

  useEffect(() => {
    loadReport();
  }, [loadReport, refreshKey]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleApplyFilters = (event) => {
    event.preventDefault();

    setPage(0);

    setAppliedFilters({
      ...filters,
      search: filters.search.trim(),
    });
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      await downloadPreparedMixesReport(appliedFilters);
    } catch (exportError) {
      setError(getApiErrorMessage(exportError));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Historia przygotowanych miksów
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Historia fizycznie przygotowanych dostaw wraz z informacją o
          użytkowniku, rewizji oraz zmianach wykrytych po przygotowaniu.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        component="form"
        variant="outlined"
        onSubmit={handleApplyFilters}
        sx={{
          p: 2,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Filtry
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <TextField
              name="preparedFrom"
              label="Przygotowano od"
              type="date"
              value={filters.preparedFrom}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth
            />

            <TextField
              name="preparedTo"
              label="Przygotowano do"
              type="date"
              value={filters.preparedTo}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
              fullWidth
            />

            <TextField
              name="search"
              label="Dostawa, odbiorca lub użytkownik"
              value={filters.search}
              onChange={handleFilterChange}
              size="small"
              fullWidth
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="prepared-report-sandwiching-label">
                Kanapkowanie
              </InputLabel>

              <Select
                labelId="prepared-report-sandwiching-label"
                name="sandwichingRequired"
                label="Kanapkowanie"
                value={filters.sandwichingRequired}
                onChange={handleFilterChange}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="true">Tak</MenuItem>
                <MenuItem value="false">Nie</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="prepared-report-changed-label">
                Zmiana po przygotowaniu
              </InputLabel>

              <Select
                labelId="prepared-report-changed-label"
                name="changedAfterPreparation"
                label="Zmiana po przygotowaniu"
                value={filters.changedAfterPreparation}
                onChange={handleFilterChange}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="true">Tak</MenuItem>
                <MenuItem value="false">Nie</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="prepared-report-review-label">
                Wymaga sprawdzenia
              </InputLabel>

              <Select
                labelId="prepared-report-review-label"
                name="requiresReview"
                label="Wymaga sprawdzenia"
                value={filters.requiresReview}
                onChange={handleFilterChange}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="true">Tak</MenuItem>
                <MenuItem value="false">Nie</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel id="prepared-report-active-label">
                Aktywna dostawa
              </InputLabel>

              <Select
                labelId="prepared-report-active-label"
                name="isActive"
                label="Aktywna dostawa"
                value={filters.isActive}
                onChange={handleFilterChange}
              >
                <MenuItem value="">Wszystkie</MenuItem>
                <MenuItem value="true">Tak</MenuItem>
                <MenuItem value="false">Nie</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1}>
              <Button type="submit" variant="contained">
                Zastosuj filtry
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={handleClearFilters}
              >
                Wyczyść
              </Button>
            </Stack>

            <Button
              type="button"
              variant="outlined"
              onClick={handleExport}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={18} /> : undefined}
            >
              {exporting ? 'Generowanie...' : 'Pobierz Excel'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        <SummaryCard
          label="Liczba przygotowań"
          value={formatInteger(summary.totalPreparations)}
        />

        <SummaryCard
          label="Pozycje miksowe"
          value={formatInteger(summary.totalMixItems)}
        />

        <SummaryCard
          label="Łączna ilość miksowa"
          value={formatQuantity(summary.totalMixQuantity)}
        />
      </Box>

      <Paper variant="outlined">
        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1750,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Numer dostawy</TableCell>
                <TableCell>Data dostawy</TableCell>
                <TableCell>Odbiorca</TableCell>
                <TableCell align="center">Kanapkowanie</TableCell>
                <TableCell align="right">Pozycje miksowe</TableCell>
                <TableCell align="right">Ilość miksowa</TableCell>
                <TableCell>Rozpoczęto</TableCell>
                <TableCell>Przygotowano</TableCell>
                <TableCell align="right">Czas [min]</TableCell>
                <TableCell>Przygotował</TableCell>
                <TableCell align="center">Rewizja</TableCell>
                <TableCell>Status bieżący</TableCell>
                <TableCell align="center">Nowsza wersja</TableCell>
                <TableCell align="center">Wymaga sprawdzenia</TableCell>
                <TableCell align="center">Aktywna</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        py: 4,
                      }}
                    >
                      <CircularProgress size={22} />

                      <Typography variant="body2" color="text.secondary">
                        Ładowanie raportu...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        py: 4,
                      }}
                    >
                      Brak przygotowanych miksów spełniających wybrane filtry.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      backgroundColor: row.requiresReview
                        ? 'error.50'
                        : row.changedSincePreparation
                        ? 'warning.50'
                        : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>
                        {row.deliveryNumber}
                      </Typography>

                      {row.deliveryType && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {row.deliveryType}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>{formatDate(row.deliveryDate)}</TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.recipientNumber || '—'}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        {row.recipientName || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <BooleanChip value={row.sandwichingRequired} />
                    </TableCell>

                    <TableCell align="right">
                      {formatInteger(row.mixItemCount)}
                    </TableCell>

                    <TableCell align="right">
                      {formatQuantity(row.totalMixQuantity)}
                    </TableCell>

                    <TableCell>{formatDateTime(row.startedAt)}</TableCell>

                    <TableCell>{formatDateTime(row.preparedAt)}</TableCell>

                    <TableCell align="right">
                      {row.preparationDurationMinutes ?? '—'}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {row.preparedBy?.name ||
                          row.preparedBy?.email ||
                          row.preparedBy?.id ||
                          '—'}
                      </Typography>

                      {row.preparedBy?.role && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          {row.preparedBy.role}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {row.preparedRevisionNumber}
                        {row.currentRevisionNumber !==
                          row.preparedRevisionNumber &&
                          ` → ${row.currentRevisionNumber}`}
                      </Typography>

                      {row.currentlyAcceptedRevisionNumber != null && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          zaakceptowana: {row.currentlyAcceptedRevisionNumber}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <StatusChip status={row.currentStatus} />
                    </TableCell>

                    <TableCell align="center">
                      <BooleanChip
                        value={row.changedSincePreparation}
                        trueColor="warning"
                      />
                    </TableCell>

                    <TableCell align="center">
                      <BooleanChip
                        value={row.requiresReview}
                        trueColor="error"
                      />
                    </TableCell>

                    <TableCell align="center">
                      <BooleanChip
                        value={row.isActive}
                        trueColor="success"
                        falseColor="default"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} z ${count !== -1 ? count : `ponad ${to}`}`
          }
        />
      </Paper>
    </Stack>
  );
}

function SummaryCard({ label, value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>

      <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function BooleanChip({ value, trueColor = 'success', falseColor = 'default' }) {
  return (
    <Chip
      size="small"
      label={value ? 'TAK' : 'NIE'}
      color={value ? trueColor : falseColor}
      variant={value ? 'filled' : 'outlined'}
    />
  );
}

function StatusChip({ status }) {
  const configuration = {
    TO_PREPARE: {
      label: 'Do przygotowania',
      color: 'warning',
    },

    IN_PROGRESS: {
      label: 'W trakcie',
      color: 'info',
    },

    PREPARED: {
      label: 'Przygotowany',
      color: 'success',
    },
  };

  const current = configuration[status] ?? {
    label: status || '—',
    color: 'default',
  };

  return <Chip size="small" label={current.label} color={current.color} />;
}

function formatInteger(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 0,
  }).format(number);
}

function formatQuantity(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0';
  }

  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(number);
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
